import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { FaChevronLeft, FaChevronRight, FaTimes, FaPen, FaExternalLinkAlt } from "react-icons/fa";
import { fetchQuestionById } from "../../pages/services/questionService";
import QuestionView from "./QuestionView";
import UpdateQuestion from "../UpdateQuestions";
import CopyableId from "../ReusableComponent/CopyableId";
import { plainText } from "./questionUtils";
import "../../styles/QuestionPreviewModal.css";

// The question list's preview. Opens on the clicked row and steps through the
// list's own result set — same filters, same sort — with Previous / Next (or
// the ← → keys), crossing page boundaries by loading the neighbouring page with
// fetchPage(pageIndex). Edit swaps in the full edit form in place.
//
// Props
//   rows, page, pageSize, total   the list's current page and its position
//   startIndex                    the clicked row within `rows`
//   fetchPage(pageIndex)          → Promise<rows> for any page, same filters/sort
//   onClose(lastPage)             the list jumps to the page last viewed
//   onChanged()                   a question was edited or deleted — refresh
const QuestionPreviewModal = ({ rows, page, pageSize, total, startIndex, fetchPage, onClose, onChanged }) => {
  const navigate = useNavigate();
  const pages = useRef({ [page]: rows });
  const [cursor, setCursor] = useState({ page, index: startIndex });
  const [question, setQuestion] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [mode, setMode] = useState("view");          // "view" | "edit"
  const [stepping, setStepping] = useState(false);
  const bodyRef = useRef(null);
  const dialogRef = useRef(null);

  const rowAt = (c) => pages.current[c.page]?.[c.index];
  const position = cursor.page * pageSize + cursor.index + 1;
  const hasPrev = position > 1;
  const hasNext = position < total;

  const load = useCallback(async (id) => {
    setLoading(true);
    setError("");
    try {
      setQuestion(await fetchQuestionById(id));
    } catch (_) {
      setError("This question could not be loaded.");
    } finally {
      setLoading(false);
      bodyRef.current?.scrollTo({ top: 0 });
    }
  }, []);

  useEffect(() => {
    const row = rowAt(cursor);
    if (row) load(row._id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cursor, load]);

  const pageRows = async (p) => {
    if (!pages.current[p]) pages.current[p] = await fetchPage(p);
    return pages.current[p];
  };

  const step = useCallback(async (dir) => {
    if (mode !== "view" || stepping) return;
    if (dir > 0 && !hasNext) return;
    if (dir < 0 && !hasPrev) return;
    const current = pages.current[cursor.page] || [];
    if (dir > 0 && cursor.index + 1 < current.length) { setCursor({ page: cursor.page, index: cursor.index + 1 }); return; }
    if (dir < 0 && cursor.index > 0) { setCursor({ page: cursor.page, index: cursor.index - 1 }); return; }
    // Crossing into the neighbouring page.
    setStepping(true);
    try {
      const target = cursor.page + dir;
      const list = await pageRows(target);
      if (list.length) setCursor({ page: target, index: dir > 0 ? 0 : list.length - 1 });
    } catch (_) {
      setError("Could not load the next page of questions.");
    } finally {
      setStepping(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cursor, mode, stepping, hasNext, hasPrev]);

  const close = useCallback(() => onClose(cursor.page), [onClose, cursor.page]);

  // ← → step, Esc closes — not while typing, and not while editing.
  useEffect(() => {
    const onKey = (e) => {
      if (mode !== "view") return;
      const tag = (e.target.tagName || "").toLowerCase();
      if (["input", "textarea", "select"].includes(tag) || e.target.isContentEditable) return;
      if (e.key === "ArrowRight") { e.preventDefault(); step(1); }
      else if (e.key === "ArrowLeft") { e.preventDefault(); step(-1); }
      else if (e.key === "Escape") close();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [step, close, mode]);

  // Take focus on open, so ← → work straight away — focus left in the search
  // box would otherwise swallow them (typing keys are ignored on purpose).
  useEffect(() => { dialogRef.current?.focus(); }, []);

  // The page behind does not scroll while the modal is open.
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = prev; };
  }, []);

  const row = rowAt(cursor);
  const title = plainText(question?.question || row?.question);

  const afterSave = async () => {
    setMode("view");
    if (question) await load(question._id);
    // The list row may have changed too (type, difficulty, classification…).
    // The fresh page replaces the cached one only once it has arrived — never
    // leave the current row missing in between.
    try {
      const fresh = await fetchPage(cursor.page);
      if (fresh.length) pages.current[cursor.page] = fresh;
    } catch (_) { /* the cached page still works */ }
    onChanged?.();
  };
  const afterDelete = () => {
    onChanged?.();
    onClose(cursor.page);
  };

  const editing = mode === "edit";

  return (
    <div className="qpm-overlay" onMouseDown={(e) => { if (e.target === e.currentTarget && !editing) close(); }}>
      <div className="qpm" role="dialog" aria-modal="true" aria-label="Question preview" tabIndex={-1} ref={dialogRef}>
        <header className="qpm-head">
          <button type="button" className="qpm-nav" onClick={() => step(-1)} disabled={!hasPrev || editing || stepping}
                  title={editing ? "Save or cancel your changes first" : "Previous question (←)"}>
            <FaChevronLeft /> <span>Previous</span>
          </button>

          <div className="qpm-head-center">
            <div className="qpm-position">Question {position.toLocaleString()} of {total.toLocaleString()}</div>
            <div className="qpm-title" title={title}>{editing ? "Editing" : title || "Untitled question"}</div>
          </div>

          <button type="button" className="qpm-nav" onClick={() => step(1)} disabled={!hasNext || editing || stepping}
                  title={editing ? "Save or cancel your changes first" : "Next question (→)"}>
            <span>Next</span> <FaChevronRight />
          </button>
          <button type="button" className="qpm-close" onClick={close} aria-label="Close" title="Close (Esc)">
            <FaTimes />
          </button>
        </header>

        {!editing && (
          <div className="qpm-toolbar">
            <button type="button" className="qpm-btn qpm-btn-primary" onClick={() => setMode("edit")} disabled={!question}>
              <FaPen /> Edit here
            </button>
            <button type="button" className="qpm-btn qpm-btn-ghost" onClick={() => question && navigate(`/questions/${question._id}`)} disabled={!question}>
              <FaExternalLinkAlt /> Open detailed view
            </button>
            {/* The row's id, so it is right the moment ← → moves on. */}
            <CopyableId id={row?._id || question?._id} label="Question ID" />
            <span className="qpm-hint">Use ← → to move between questions</span>
          </div>
        )}

        <div className="qpm-body" ref={bodyRef}>
          {error ? (
            <div className="qpm-state">{error}</div>
          ) : loading && !question ? (
            <div className="qpm-state"><div className="qpm-spinner" />Loading question…</div>
          ) : editing && question ? (
            <UpdateQuestion
              key={question._id}
              questionId={question._id}
              embedded
              onSaved={afterSave}
              onCancel={() => setMode("view")}
              onDeleted={afterDelete}
            />
          ) : (
            <div className={loading ? "qpm-fading" : ""}>
              <QuestionView question={question} compact />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default QuestionPreviewModal;
